/**
 * De vaste periodes die het behandeltijdscherm aanbiedt.
 *
 * Allemaal in HELE DAGEN en met de tot-datum MEEGETELD, zoals het scherm ze toont: "van 1 tot en
 * met 31" is die hele maand. De serverlaag vertaalt dat naar het half-open venster dat de bron
 * verwacht.
 *
 * > [!warning] De bron weigert elke periode langer dan een jaar
 * > Dat is geen stijlkeuze: er is geen zoekversnelling op de persoonskolom of op het tijdstip. De
 * > grens stond op 31 dagen tot de query-analyse gedraaid was; die meting is op 25 augustus 2026
 * > gedaan op de productiedatabase en staat vastgelegd bij `MAX_PERIOD_DAYS` in de bron. Elke keuze
 * > hieronder past
 * > daar met opzet binnen. De langste is "dit jaar", en die kan bestaan sinds de grens op
 * > 25 augustus 2026 naar 366 dagen ging — een jaar en een dag, zodat een schrikkeljaar past.
 */

export type PeriodePreset = 'gisteren' | 'dezeWeek' | 'dezeMaand' | 'laatste30Dagen' | 'ditJaar';

/** Waar het scherm mee begint als er niets gekozen is. */
export const STANDAARD_PRESET: PeriodePreset = 'laatste30Dagen';

/** De volgorde in de keuzelijst: van kort naar lang, zodat de standaard onderaan staat. */
export const PRESETS: PeriodePreset[] = ['gisteren', 'dezeWeek', 'dezeMaand', 'laatste30Dagen', 'ditJaar'];

export interface Periode {
  from: string;
  to: string;
}

/** De tijdzone waarin het werk op de dag gerekend wordt. Niet die van de browser. */
const AMSTERDAM = 'Europe/Amsterdam';

const kalenderdag = new Intl.DateTimeFormat('en-CA', {
  timeZone: AMSTERDAM,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * De Amsterdamse kalenderdag van een moment, als UTC-middernacht.
 *
 * "Vandaag" is het moment zelf in de kalender van Amsterdam, niet die van de browser: een browser
 * op UTC ziet om 00:30 Amsterdam nog de vorige dag. Het resultaat is een UTC-middernacht zodat het
 * rekenen met dagen geen zomertijd kent — de kalendervelden zijn daarbij het enige dat telt.
 */
function amsterdamDag(moment: Date): Date {
  const delen = Object.fromEntries(kalenderdag.formatToParts(moment).map((deel) => [deel.type, deel.value]));
  return new Date(Date.UTC(Number(delen.year), Number(delen.month) - 1, Number(delen.day)));
}

/** Een datum als `JJJJ-MM-DD`, van een UTC-middernacht uit `amsterdamDag`. */
function alsDatum(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Dagen optellen of aftrekken op kalenderwaarden.
 *
 * `new Date(t - n * 86400000)` telt in milliseconden, en een etmaal is twee keer per jaar 23 of 25
 * uur lang. Dan komt "dertig dagen terug" op de verkeerde dag uit. Via de kalendervelden gaat dat
 * niet mis.
 */
function verschuif(d: Date, dagen: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + dagen));
}

/**
 * De periode die bij een keuze hoort, gerekend vanaf vandaag.
 *
 * `vandaag` is een parameter en geen aanroep van `new Date()` binnenin: zo is elke keuze te toetsen
 * op een vaste dag, inclusief maandgrenzen, jaargrenzen en een schrikkeldag.
 */
export function periodeVoor(preset: PeriodePreset, vandaagMoment: Date): Periode {
  const vandaag = amsterdamDag(vandaagMoment);
  switch (preset) {
    case 'gisteren': {
      const gisteren = verschuif(vandaag, -1);
      return { from: alsDatum(gisteren), to: alsDatum(gisteren) };
    }
    case 'dezeWeek': {
      // Maandag als eerste dag: dat is hoe hier over een werkweek gepraat wordt. `getDay()` geeft
      // zondag als 0, dus die telt bij de week die eraan voorafgaat.
      const dagNummer = vandaag.getUTCDay();
      const dagenTerug = dagNummer === 0 ? 6 : dagNummer - 1;
      return { from: alsDatum(verschuif(vandaag, -dagenTerug)), to: alsDatum(vandaag) };
    }
    case 'dezeMaand': {
      const eerste = new Date(Date.UTC(vandaag.getUTCFullYear(), vandaag.getUTCMonth(), 1));
      return { from: alsDatum(eerste), to: alsDatum(vandaag) };
    }
    case 'ditJaar': {
      // Van 1 januari tot en met vandaag. Dat is hoogstens 366 dagen, en dat is precies de grens
      // van de bron sinds de meting van 25 augustus 2026.
      const eersteVanHetJaar = new Date(Date.UTC(vandaag.getUTCFullYear(), 0, 1));
      return { from: alsDatum(eersteVanHetJaar), to: alsDatum(vandaag) };
    }
    case 'laatste30Dagen':
      // Dertig dagen MET vandaag erbij, dus negenentwintig terug. Anders is het er eenendertig en
      // schuurt het tegen de grens van de bron aan.
      return { from: alsDatum(verschuif(vandaag, -29)), to: alsDatum(vandaag) };
  }
}

/**
 * Welke keuze bij een periode hoort, of `null` als het geen van alle is.
 *
 * Nodig omdat de periode in het webadres staat: wie een gedeelde link opent of zelf datums invult,
 * moet de keuzelijst zien staan op wat er werkelijk getoond wordt — en op "aangepast" zodra het
 * geen vaste keuze meer is.
 */
export function presetVoor(periode: Periode, vandaag: Date, voorkeur: PeriodePreset = STANDAARD_PRESET): PeriodePreset | null {
  // Twee keuzes kunnen dezelfde periode opleveren: op 30 augustus loopt "deze maand" van 1 tot en
  // met 30 augustus, en dat zijn precies de laatste dertig dagen. Dan is er geen goede volgorde,
  // alleen een goede voorkeur — die van de gebruiker als hij zelf koos, en anders de standaard.
  // Zonder dat ziet iemand die de pagina zonder eigen periode opent "deze maand" staan terwijl hij
  // "laatste 30 dagen" kreeg, of springt zijn eigen keuze terug naar de standaard.
  const zoekvolgorde = [voorkeur, ...PRESETS.filter((preset) => preset !== voorkeur)];
  return zoekvolgorde.find((preset) => {
    const p = periodeVoor(preset, vandaag);
    return p.from === periode.from && p.to === periode.to;
  }) ?? null;
}
